/**
 * PATH: packages/ui/src/components/PromptDialog.tsx
 * FILE_ID: UI-PROMPT-DIALOG-001
 * PURPOSE: Themed, accessible replacement for the native window.prompt()
 *          browser modal. Two APIs in one module (mirrors ConfirmDialog):
 *
 *   1. <PromptDialog open onSubmit onCancel /> — controlled React component.
 *      Pair with your own useState('open') + value.
 *   2. await promptDialog({ title, defaultValue, ... }) — imperative
 *      Promise<string | null>. Resolves with the trimmed string the
 *      user typed, or `null` if they cancelled / closed.
 *
 * WHY: Native window.prompt renders as an OS-level alert
 *      ("dashboard.quotech.ai says…"), can't be styled, is not keyboard
 *      / screen-reader friendly, is jarring on touch devices, and
 *      can't be branded. This wrapper looks like the rest of our UI
 *      (matches ConfirmDialog) so estimators stop being yanked into
 *      a different visual world every time they need to type a reason.
 * FLOW:
 *   ┌──────────────────────┐   ┌────────────────────┐   ┌──────────────────────┐
 *   │ promptDialog({...})  │ → │ <PromptDialog open/> │ → │ Promise<string|null> │
 *   └──────────────────────┘   └────────────────────┘   └──────────────────────┘
 * DEPENDENCIES:
 *   - react / react-dom: createPortal, createRoot
 *   - lucide-react: pencil icon header decoration
 *   - ../lib/utils cn: class merging
 * RELATED:
 *   - ConfirmDialog.tsx: sister primitive, same visual shell. PromptDialog
 *     intentionally re-uses its layout (header gutter, footer bar, button
 *     styling) so the two never look out of place when chained.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import type { FormEvent, ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { createRoot } from 'react-dom/client'
import { Pencil } from 'lucide-react'

import { cn } from '../lib/utils'

export type PromptDialogVariant = 'default' | 'danger' | 'warning'

export interface PromptDialogProps {
  open: boolean
  title: string
  /** Body text shown above the input. Plain string or any ReactNode. */
  description?: ReactNode
  /** Label rendered above the input. Defaults to no label. */
  inputLabel?: string
  /** Pre-fill the input with this string. Defaults to ''. */
  defaultValue?: string
  /** Placeholder for the input. */
  placeholder?: string
  /** When true, render a `<textarea>` instead of a single-line input. */
  multiline?: boolean
  /** Required input — disables Submit while empty. Defaults to false. */
  required?: boolean
  /** Trim trailing whitespace before submit. Defaults to true. */
  trim?: boolean
  submitLabel?: string
  cancelLabel?: string
  /** Visual emphasis — danger uses red Submit, warning amber. */
  variant?: PromptDialogVariant
  /**
   * Optional client-side validator. Return a string to render an inline
   * error and block submit. Return null/undefined to accept the value.
   */
  validate?: (value: string) => string | null | undefined
  onSubmit: (value: string) => void
  onCancel: () => void
}

const VARIANT_BUTTON_CLASS: Record<PromptDialogVariant, string> = {
  default: 'bg-primary text-primary-foreground hover:bg-primary/90',
  danger:
    'bg-destructive text-destructive-foreground hover:bg-destructive/90',
  warning: 'bg-amber-600 text-white hover:bg-amber-700',
}

export function PromptDialog({
  open,
  title,
  description,
  inputLabel,
  defaultValue = '',
  placeholder,
  multiline = false,
  required = false,
  trim = true,
  submitLabel = 'Submit',
  cancelLabel = 'Cancel',
  variant = 'default',
  validate,
  onSubmit,
  onCancel,
}: PromptDialogProps) {
  const [value, setValue] = useState(defaultValue)
  const [error, setError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null)

  // Reset state every time the dialog opens — otherwise a second open
  // with a different defaultValue would still show the previous typed
  // value (the component instance can be re-used inside imperative mounts).
  useEffect(() => {
    if (open) {
      setValue(defaultValue)
      setError(null)
      // Focus + select after the open animation gets a chance to start.
      const t = setTimeout(() => {
        const el = inputRef.current
        if (!el) return
        el.focus()
        // For pre-filled inputs (e.g. rename) select all so typing replaces.
        if (defaultValue) {
          try {
            (el as HTMLInputElement).select()
          } catch {
            /* no-op — textareas don't always support .select() */
          }
        }
      }, 20)
      return () => clearTimeout(t)
    }
    return undefined
  }, [open, defaultValue])

  const tryCommit = useCallback(() => {
    const out = trim ? value.trim() : value
    if (required && !out) {
      setError('This field is required.')
      return
    }
    const validationError = validate?.(out)
    if (validationError) {
      setError(validationError)
      return
    }
    onSubmit(out)
  }, [value, trim, required, validate, onSubmit])

  // Cancel on Escape; Enter submits (single-line only — in textarea Enter
  // inserts a newline like a sane multi-line editor).
  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        onCancel()
      } else if (e.key === 'Enter' && !multiline && !e.shiftKey) {
        e.preventDefault()
        tryCommit()
      } else if (e.key === 'Enter' && multiline && (e.metaKey || e.ctrlKey)) {
        // ⌘/Ctrl + Enter submits a multi-line field — Excel-style.
        e.preventDefault()
        tryCommit()
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [open, multiline, onCancel, tryCommit])

  const handleFormSubmit = (e: FormEvent) => {
    e.preventDefault()
    tryCommit()
  }

  if (!open) return null

  const trimmedForState = trim ? value.trim() : value
  const submitDisabled = required && !trimmedForState

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-foreground/40 p-4 backdrop-blur-[2px] animate-in fade-in duration-150"
      onClick={onCancel}
    >
      <form
        onSubmit={handleFormSubmit}
        className="w-full max-w-md overflow-hidden rounded-2xl border border-border bg-card shadow-2xl animate-in zoom-in-95 duration-150"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 p-5">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Pencil className="h-5 w-5" aria-hidden />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[14px] font-semibold text-foreground">{title}</p>
            {description ? (
              <div className="mt-1 text-[13px] leading-snug text-muted-foreground">
                {description}
              </div>
            ) : null}

            {inputLabel ? (
              <label className="mt-3 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                {inputLabel}
              </label>
            ) : null}

            <div className={cn('mt-2', !inputLabel && 'mt-3')}>
              {multiline ? (
                <textarea
                  ref={(el) => { inputRef.current = el }}
                  value={value}
                  onChange={(e) => { setValue(e.target.value); if (error) setError(null) }}
                  placeholder={placeholder}
                  rows={4}
                  className={cn(
                    'w-full resize-y rounded-lg border bg-background px-3 py-2 text-[13px] text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-primary/40',
                    error ? 'border-destructive' : 'border-border',
                  )}
                />
              ) : (
                <input
                  ref={(el) => { inputRef.current = el }}
                  type="text"
                  value={value}
                  onChange={(e) => { setValue(e.target.value); if (error) setError(null) }}
                  placeholder={placeholder}
                  className={cn(
                    'w-full rounded-lg border bg-background px-3 py-2 text-[13px] text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-primary/40',
                    error ? 'border-destructive' : 'border-border',
                  )}
                />
              )}
            </div>

            {error ? (
              <p className="mt-1.5 text-[12px] text-destructive">{error}</p>
            ) : (
              <p className="mt-1.5 text-[11px] text-muted-foreground/70">
                {multiline ? 'Press ⌘/Ctrl + Enter to submit.' : 'Press Enter to submit, Esc to cancel.'}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-border bg-muted/20 px-4 py-2.5">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg border border-border bg-card px-3 py-1.5 text-[12px] font-medium text-foreground transition-colors hover:bg-muted"
          >
            {cancelLabel}
          </button>
          <button
            type="submit"
            disabled={submitDisabled}
            className={cn(
              'rounded-lg px-3 py-1.5 text-[12px] font-semibold shadow-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50',
              VARIANT_BUTTON_CLASS[variant],
            )}
          >
            {submitLabel}
          </button>
        </div>
      </form>
    </div>,
    document.body,
  )
}

/* ── Imperative API ─────────────────────────────────────── */

export interface PromptDialogOptions {
  title: string
  description?: ReactNode
  inputLabel?: string
  defaultValue?: string
  placeholder?: string
  multiline?: boolean
  required?: boolean
  trim?: boolean
  submitLabel?: string
  cancelLabel?: string
  variant?: PromptDialogVariant
  validate?: (value: string) => string | null | undefined
}

/**
 * Imperative prompt — returns a Promise<string | null>. Mounts a
 * <PromptDialog> via createRoot, resolves with the typed string on
 * Submit, or `null` on Cancel / Escape / backdrop-click.
 *
 * Drop-in replacement for `window.prompt()`:
 *   const reason = await promptDialog({ title: 'Reason:', required: true })
 *   if (reason === null) return       // user cancelled
 *   doSomething(reason)
 */
export function promptDialog(options: PromptDialogOptions): Promise<string | null> {
  return new Promise((resolve) => {
    const container = document.createElement('div')
    document.body.appendChild(container)
    const root = createRoot(container)

    const cleanup = () => {
      // Defer unmount past the close animation (150ms) so the exit
      // transition can finish without flashing a blank overlay rect.
      setTimeout(() => {
        root.unmount()
        container.remove()
      }, 200)
    }

    const handleSubmit = (value: string) => {
      resolve(value)
      root.render(
        <PromptDialog
          {...options}
          open={false}
          onSubmit={() => {}}
          onCancel={() => {}}
        />,
      )
      cleanup()
    }

    const handleCancel = () => {
      resolve(null)
      root.render(
        <PromptDialog
          {...options}
          open={false}
          onSubmit={() => {}}
          onCancel={() => {}}
        />,
      )
      cleanup()
    }

    root.render(
      <PromptDialog
        {...options}
        open
        onSubmit={handleSubmit}
        onCancel={handleCancel}
      />,
    )
  })
}

/* ── Hook API ───────────────────────────────────────────── */

export interface UsePromptDialogOptions extends Omit<PromptDialogOptions, 'title'> {
  title: string
}

export interface UsePromptDialogReturn {
  /** Open the dialog and wait for the user's answer.
   * Resolves to the typed string on Submit, or null on Cancel. */
  prompt: (options: UsePromptDialogOptions) => Promise<string | null>
  /** Spread onto a `<PromptDialog>` rendered in your component tree. */
  dialogProps: PromptDialogProps
}

/**
 * Hook variant of `promptDialog()` — returns `{ prompt, dialogProps }`.
 * Use when you want the dialog DOM to live inside your component tree
 * (better focus management + provider scope) instead of escaping via
 * a createRoot portal.
 */
export function usePromptDialog(): UsePromptDialogReturn {
  const [state, setState] = useState<UsePromptDialogOptions & { open: boolean }>(
    { open: false, title: '' },
  )
  const resolveRef = useRef<((value: string | null) => void) | null>(null)

  const prompt = useCallback(
    (options: UsePromptDialogOptions): Promise<string | null> => {
      return new Promise((resolve) => {
        resolveRef.current = resolve
        setState({ open: true, ...options })
      })
    },
    [],
  )

  const handleSubmit = useCallback((value: string) => {
    resolveRef.current?.(value)
    resolveRef.current = null
    setState((prev) => ({ ...prev, open: false }))
  }, [])

  const handleCancel = useCallback(() => {
    resolveRef.current?.(null)
    resolveRef.current = null
    setState((prev) => ({ ...prev, open: false }))
  }, [])

  const dialogProps: PromptDialogProps = {
    open: state.open,
    title: state.title,
    description: state.description,
    inputLabel: state.inputLabel,
    defaultValue: state.defaultValue,
    placeholder: state.placeholder,
    multiline: state.multiline,
    required: state.required,
    trim: state.trim,
    submitLabel: state.submitLabel,
    cancelLabel: state.cancelLabel,
    variant: state.variant,
    validate: state.validate,
    onSubmit: handleSubmit,
    onCancel: handleCancel,
  }

  return { prompt, dialogProps }
}
